import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  NgZone,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  VlResearchCategory,
  VlResearchDecision,
  VlResearchFinding,
  VlResearchItem,
  VlResearchNoteItem,
  VlResearchNotesResponse,
  VlResearchResponse,
  VlResearchStatus,
} from 'src/app/models/valuationLab';
import { ValuationLabService, VlError } from 'src/app/services/valuation-lab-service';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const PAGE_SIZE = 50;
const BATCH_SIZE = 20;
const NOTES_MAX = 200;
const DRAFTS_KEY = 'pla-vlab-research-drafts';
export const OWNER_NOTE_SOURCE = 'owner note, structured by Claude';

/** One batch-entry row being typed. Persisted to localStorage until saved. */
export interface BatchDraft {
  why: string;
  category: string;
  dontKnow: boolean;
}

export const RESEARCH_CATEGORIES: { id: VlResearchCategory; label: string }[] = [
  { id: 'car_model', label: 'Car model' },
  { id: 'car_brand', label: 'Car brand' },
  { id: 'person_name', label: 'Person / name' },
  { id: 'word', label: 'Word' },
  { id: 'initials', label: 'Initials' },
  { id: 'place', label: 'Place' },
  { id: 'number_pattern', label: 'Number pattern' },
  { id: 'date_or_year', label: 'Date or year' },
  { id: 'organisation', label: 'Organisation' },
  { id: 'pop_culture', label: 'Pop culture' },
  { id: 'other', label: 'Other' },
  { id: 'none_found', label: 'Nothing found' },
];

/**
 * Valuation Lab · Research queue — lots that sold well above the top of their
 * valuation range, with the (Claude web-search) research finding on what the
 * plate means, for the owner to accept / reject / ask for more.
 * Findings are suggestions until reviewed; a review never changes a valuation.
 * Prices on this endpoint are WHOLE POUNDS.
 */
@Component({
  selector: 'pla-vlab-research',
  templateUrl: './vlab-research.component.html',
  // Shares the Valuation Lab tokens / building blocks, plus its own layout.
  styleUrls: ['../admin-valuation-lab.component.scss', './vlab-research.component.scss'],
})
export class VlabResearchComponent implements OnInit {
  /** queued + researched (awaiting review) — shown on the parent's view switch. */
  @Output() pending = new EventEmitter<number>();
  /** Whole-queue counts by status, for the parent's breakdown line. */
  @Output() countsChange = new EventEmitter<Partial<Record<VlResearchStatus, number>>>();
  /** "Open lot in scorecard": the parent switches view and opens the lot. */
  @Output() openLot = new EventEmitter<string>();

  @ViewChild('itemPanel') itemPanel: ElementRef<HTMLElement>;

  public isPhone = false;
  public readonly categories = RESEARCH_CATEGORIES;
  public readonly statuses: { id: VlResearchStatus; label: string }[] = [
    { id: 'queued', label: 'Queued' },
    { id: 'owner_explained', label: 'Your note – awaiting Claude' },
    { id: 'research_requested', label: 'Sent to AI research' },
    { id: 'researched', label: 'Researched – awaiting review' },
    { id: 'reviewed', label: 'Reviewed' },
    { id: 'error', label: 'Error' },
  ];

  // ---- list
  public rule: string | null = null;
  public items: VlResearchItem[] = [];
  public total = 0;
  public counts: Partial<Record<VlResearchStatus, number>> = {};
  public page = 1;
  public loading = false;
  public error: string | null = null;
  public status: VlResearchStatus | '' = '';
  public category = '';
  public q = '';

  // ---- item
  public selected: VlResearchItem | null = null;
  public itemLoading = false;
  public itemError: string | null = null;

  // ---- review form
  public decision: VlResearchDecision | null = null;
  public readsAs = '';
  public reviewCategory = '';
  public note = '';
  public saving = false;
  public saved = false;
  public saveError: string | null = null;

  // ---- mode
  public mode: 'review' | 'batch' = 'review';
  private modeChosen = false;

  // ---- batch entry
  public batch: VlResearchItem[] = [];
  public batchPage = 1;
  public batchTotal = 0;
  public batchLoading = false;
  public batchError: string | null = null;
  public drafts: Record<string, BatchDraft> = {};
  public batchSaving = false;
  public batchResult: string | null = null;
  public batchSaveError: string | null = null;
  private batchSeq = 0;

  // ---- owner note editing (item panel)
  public noteEditing = false;
  public noteWhy = '';
  public noteCategory = '';
  public noteSaving = false;
  public noteSaved: string | null = null;
  public noteError: string | null = null;

  private listSeq = 0;
  private itemSeq = 0;

  constructor(
    private lab: ValuationLabService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    private hostEl: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.onResize();
    this.restoreDrafts();
    this.load(false);
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(e: BeforeUnloadEvent): void {
    if (this.dirtyCount > 0) {
      e.preventDefault();
      e.returnValue = '';
    }
  }

  // ===================================================================== mode

  public setMode(m: 'review' | 'batch'): void {
    if (m === this.mode) return;
    if (this.mode === 'batch' && this.dirtyCount > 0) {
      const ok =
        typeof window === 'undefined' ||
        window.confirm(
          'You have ' + this.dirtyCount + ' unsaved ' + (this.dirtyCount === 1 ? 'note' : 'notes') +
            '. They stay in this browser until you save. Switch anyway?'
        );
      if (!ok) return;
    }
    this.modeChosen = true;
    this.mode = m;
    if (m === 'batch' && !this.batch.length && !this.batchLoading) this.loadBatch(this.batchPage);
  }

  // ============================================================== batch entry

  public loadBatch(page: number): void {
    const seq = ++this.batchSeq;
    this.batchLoading = true;
    this.batchError = null;
    this.lab
      .research({ status: 'queued', page, page_size: BATCH_SIZE })
      .then((r) =>
        this.apply(() => {
          if (seq !== this.batchSeq) return;
          const rows = ((r && r.items) || []).filter(
            (it) => it.status === 'queued' && !it.finding && !it.owner_note && !it.research_requested
          );
          this.batchTotal = r && typeof r.total === 'number' ? r.total : rows.length;
          // Everything on this page got saved by someone else: step back.
          if (!rows.length && page > 1 && this.batchTotal > 0) {
            this.batchLoading = false;
            this.loadBatch(Math.max(1, Math.ceil(this.batchTotal / BATCH_SIZE)));
            return;
          }
          this.batch = rows;
          this.batchPage = (r && r.page) || page;
          rows.forEach((it) => this.draft(it.plate));
          if (r && r.counts) this.setCounts(r.counts);
        })
      )
      .catch((e) => this.apply(() => seq === this.batchSeq && (this.batchError = errText(e))))
      .finally(() => this.apply(() => seq === this.batchSeq && (this.batchLoading = false)));
  }

  public get batchPages(): number {
    return Math.max(1, Math.ceil(this.batchTotal / BATCH_SIZE));
  }

  public draft(plate: string): BatchDraft {
    if (!this.drafts[plate]) this.drafts[plate] = { why: '', category: '', dontKnow: false };
    return this.drafts[plate];
  }

  private hasContent(d: BatchDraft | undefined): boolean {
    return !!d && (d.dontKnow || !!d.why.trim());
  }

  /** Rows (on any page) with text or "don't know" that haven't been saved yet. */
  public get dirtyCount(): number {
    return Object.keys(this.drafts).filter((k) => this.hasContent(this.drafts[k])).length;
  }

  public get dirtyOffPage(): number {
    const here: Record<string, boolean> = {};
    this.batch.forEach((it) => (here[it.plate] = true));
    return Object.keys(this.drafts).filter((k) => !here[k] && this.hasContent(this.drafts[k])).length;
  }

  public onDraftChange(): void {
    this.batchResult = null;
    this.persistDrafts();
  }

  public toggleDontKnow(plate: string): void {
    const d = this.draft(plate);
    d.dontKnow = !d.dontKnow;
    this.onDraftChange();
  }

  public saveBatch(): void {
    if (this.batchSaving) return;
    const plates = Object.keys(this.drafts).filter((k) => this.hasContent(this.drafts[k]));
    if (!plates.length) return;
    const items: VlResearchNoteItem[] = plates.map((plate) => {
      const d = this.drafts[plate];
      if (d.dontKnow) return { plate, send_to_research: true as const };
      const it: { plate: string; why: string; category?: string } = { plate, why: d.why.trim() };
      if (d.category) it.category = d.category;
      return it;
    });
    const chunks: VlResearchNoteItem[][] = [];
    for (let i = 0; i < items.length; i += NOTES_MAX) chunks.push(items.slice(i, i + NOTES_MAX));
    this.batchSaving = true;
    this.batchSaveError = null;
    this.batchResult = null;
    const total: VlResearchNotesResponse = { ok: true, saved: [], sent_to_research: [], skipped: [] };
    chunks
      .reduce(
        (p, chunk) =>
          p.then(() =>
            this.lab.researchNotes(chunk).then((r) => {
              total.saved = total.saved.concat((r && r.saved) || []);
              total.sent_to_research = total.sent_to_research.concat((r && r.sent_to_research) || []);
              total.skipped = total.skipped.concat((r && r.skipped) || []);
            })
          ),
        Promise.resolve()
      )
      .then(() =>
        this.apply(() => {
          // Saved / sent / skipped rows leave the batch; skipped plates are named in the message.
          total.saved.concat(total.sent_to_research, total.skipped).forEach((p) => delete this.drafts[p]);
          this.persistDrafts();
          const n = total.saved.length;
          const m = total.sent_to_research.length;
          const k = total.skipped.length;
          this.batchResult =
            'Saved ' + n + ' ' + (n === 1 ? 'note' : 'notes') + ' · sent ' + m + ' to research · skipped ' + k +
            (k ? ' (' + total.skipped.slice(0, 8).join(', ') + (k > 8 ? '…' : '') + ')' : '');
          this.loadBatch(this.batchPage);
          if (this.mode === 'review') this.load(false);
        })
      )
      .catch((e) =>
        this.apply(() => {
          // Partial success is possible across chunks: drop what the server confirmed.
          total.saved.concat(total.sent_to_research).forEach((p) => delete this.drafts[p]);
          this.persistDrafts();
          this.batchSaveError = errText(e) + ' — unsaved rows are kept.';
        })
      )
      .finally(() => this.apply(() => (this.batchSaving = false)));
  }

  /** Tab / Shift+Tab hop between the "why" boxes; Alt+K toggles "don't know". */
  public onWhyKeydown(e: KeyboardEvent, i: number, plate: string): void {
    if (e.key === 'Tab' && !e.altKey && !e.ctrlKey && !e.metaKey) {
      const next = i + (e.shiftKey ? -1 : 1);
      const el = typeof document !== 'undefined' ? (document.getElementById('why-' + next) as HTMLTextAreaElement) : null;
      if (el) {
        e.preventDefault();
        el.focus();
      }
    } else if (e.altKey && (e.key === 'k' || e.key === 'K' || e.code === 'KeyK')) {
      e.preventDefault();
      this.toggleDontKnow(plate);
      // the box is now disabled: keep keyboard focus on this row's toggle
      setTimeout(() => {
        const cb = typeof document !== 'undefined' ? document.getElementById('dk-' + i) : null;
        if (cb) cb.focus();
      }, 0);
    }
  }

  /** Alt+K on a row's toggle flips it back and returns to the box. */
  public onToggleKeydown(e: KeyboardEvent, i: number, plate: string): void {
    if (e.altKey && (e.key === 'k' || e.key === 'K' || e.code === 'KeyK')) {
      e.preventDefault();
      this.toggleDontKnow(plate);
      setTimeout(() => {
        const t = typeof document !== 'undefined' ? (document.getElementById('why-' + i) as HTMLTextAreaElement) : null;
        if (t && !t.disabled) t.focus();
      }, 0);
    }
  }

  /** Ctrl/Cmd+Enter anywhere on the page saves the batch while batch entry is open. */
  @HostListener('document:keydown', ['$event'])
  public onBatchKeydown(e: KeyboardEvent): void {
    // only while batch entry is actually on screen (the view is kept mounted when hidden)
    if (this.mode !== 'batch' || !this.hostEl.nativeElement.offsetParent) return;
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      this.saveBatch();
    }
  }

  private persistDrafts(): void {
    try {
      const keep: Record<string, BatchDraft> = {};
      Object.keys(this.drafts).forEach((k) => this.hasContent(this.drafts[k]) && (keep[k] = this.drafts[k]));
      localStorage.setItem(DRAFTS_KEY, JSON.stringify(keep));
    } catch {
      /* storage unavailable: drafts live in memory only */
    }
  }

  private restoreDrafts(): void {
    try {
      const raw = localStorage.getItem(DRAFTS_KEY);
      const d = raw ? JSON.parse(raw) : null;
      if (d && typeof d === 'object') {
        Object.keys(d).forEach((k) => {
          const v = d[k];
          if (v && typeof v === 'object')
            this.drafts[k] = { why: String(v.why || ''), category: String(v.category || ''), dontKnow: !!v.dontKnow };
        });
      }
    } catch {
      /* ignore */
    }
  }

  // ======================================================== owner note (item)

  public startNoteEdit(it: VlResearchItem): void {
    const n = it.owner_note || (it.finding && it.finding.owner_note) || null;
    this.noteWhy = (n && n.why) || '';
    this.noteCategory = (n && n.category) || '';
    this.noteEditing = true;
    this.noteSaved = null;
    this.noteError = null;
  }

  public cancelNoteEdit(): void {
    this.noteEditing = false;
  }

  public saveNote(it: VlResearchItem, sendToResearch = false): void {
    if (this.noteSaving) return;
    const why = this.noteWhy.trim();
    if (!sendToResearch && !why) return;
    const item: VlResearchNoteItem = sendToResearch
      ? { plate: it.plate, send_to_research: true }
      : this.noteCategory
      ? { plate: it.plate, why, category: this.noteCategory }
      : { plate: it.plate, why };
    this.noteSaving = true;
    this.noteError = null;
    this.noteSaved = null;
    this.lab
      .researchNotes([item])
      .then((r) =>
        this.apply(() => {
          if (r && r.skipped && r.skipped.indexOf(it.plate) >= 0) {
            this.noteError = 'Not saved — the server skipped this plate (not in the queue or empty).';
            return;
          }
          this.noteEditing = false;
          this.noteSaved = sendToResearch ? 'Sent to AI research' : 'Note saved';
          delete this.drafts[it.plate];
          this.persistDrafts();
          this.refreshCounts();
          if (this.selected && this.selected.plate === it.plate) this.select(this.selected, true);
        })
      )
      .catch((e) => this.apply(() => (this.noteError = errText(e))))
      .finally(() => this.apply(() => (this.noteSaving = false)));
  }

  public ownerNote(it: VlResearchItem | null) {
    if (!it) return null;
    return it.owner_note || (it.finding && it.finding.owner_note) || null;
  }

  public fromOwnerNote(it: VlResearchItem | null): boolean {
    const f = this.findingOk(it);
    return !!f && f.source === OWNER_NOTE_SOURCE;
  }

  @HostListener('window:resize')
  onResize(): void {
    if (typeof window === 'undefined') return;
    this.isPhone = window.innerWidth < 768;
  }

  // ===================================================================== list

  public load(append: boolean): void {
    const seq = ++this.listSeq;
    const page = append ? this.page + 1 : 1;
    this.loading = true;
    this.error = null;
    this.lab
      .research({
        status: this.status || undefined,
        category: this.category || undefined,
        q: this.q.trim() ? normPlate(this.q) : undefined,
        page,
        page_size: PAGE_SIZE,
      })
      .then((r: VlResearchResponse) =>
        this.apply(() => {
          if (seq !== this.listSeq) return;
          const rows = (r && r.items) || [];
          this.items = append ? this.items.concat(rows) : rows;
          this.total = r && typeof r.total === 'number' ? r.total : this.items.length;
          this.page = (r && r.page) || page;
          this.rule = (r && r.rule) || this.rule;
          // counts are the whole queue's, independent of the filters
          if (r && r.counts) this.setCounts(r.counts);
        })
      )
      .catch((e) =>
        this.apply(() => {
          if (seq !== this.listSeq) return;
          if (!append) this.items = [];
          this.error = errText(e);
        })
      )
      .finally(() => this.apply(() => seq === this.listSeq && (this.loading = false)));
  }

  public get canLoadMore(): boolean {
    return !this.loading && this.items.length < this.total;
  }

  public get pendingCount(): number {
    return (this.counts.queued || 0) + (this.counts.researched || 0);
  }

  private setCounts(c: Partial<Record<VlResearchStatus, number>>): void {
    this.counts = c || {};
    this.pending.emit(this.pendingCount);
    this.countsChange.emit(this.counts);
    // Default to batch entry while there are untouched queued lots (until the owner picks a mode).
    if (!this.modeChosen) {
      this.modeChosen = true;
      if ((this.counts.queued || 0) > 0) {
        this.mode = 'batch';
        if (!this.batch.length && !this.batchLoading) this.loadBatch(1);
      }
    }
  }

  public onFilterChange(): void {
    this.load(false);
  }

  // ===================================================================== item

  public select(it: VlResearchItem, keepMessages = false): void {
    const seq = ++this.itemSeq;
    this.selected = it;
    this.prefill(it);
    this.noteEditing = false;
    if (!keepMessages) {
      this.noteSaved = null;
      this.noteError = null;
    }
    this.itemLoading = true;
    this.itemError = null;
    if (this.isPhone && !keepMessages) this.scrollTo(this.itemPanel);
    this.lab
      .researchItem(it.plate)
      .then((fresh) =>
        this.apply(() => {
          if (seq !== this.itemSeq || !fresh) return;
          const merged = { ...it, ...fresh, rank: fresh.rank ?? it.rank };
          this.replaceItem(merged);
          this.selected = merged;
          this.prefill(merged);
        })
      )
      .catch((e) => this.apply(() => seq === this.itemSeq && (this.itemError = errText(e))))
      .finally(() => this.apply(() => seq === this.itemSeq && (this.itemLoading = false)));
  }

  private prefill(it: VlResearchItem): void {
    const f = this.findingOk(it);
    const r = it.review;
    this.decision = null;
    this.readsAs = (r && r.reads_as) || (f && f.reads_as) || '';
    this.reviewCategory = (r && r.category) || (f && (f.category as string)) || '';
    this.note = '';
    this.saved = false;
    this.saveError = null;
  }

  public setDecision(d: VlResearchDecision): void {
    this.decision = d;
    this.saved = false;
  }

  public saveReview(): void {
    const it = this.selected;
    if (!it || !this.decision || this.saving) return;
    this.saving = true;
    this.saved = false;
    this.saveError = null;
    const body = {
      plate: it.plate,
      decision: this.decision,
      reads_as: this.readsAs.trim() || undefined,
      category: this.reviewCategory || undefined,
      note: this.note.trim() || undefined,
    };
    this.lab
      .researchReview(body)
      .then(() =>
        this.apply(() => {
          // Show the saved review straight away; the re-fetch below confirms it.
          const updated: VlResearchItem = {
            ...it,
            status: 'reviewed',
            review: {
              decision: body.decision,
              reads_as: body.reads_as || null,
              category: body.category || null,
              note: body.note || null,
              at: new Date().toISOString(),
              by: 'you',
            },
          };
          this.replaceItem(updated);
          if (this.selected && this.selected.plate === it.plate) {
            this.selected = updated;
            this.saved = true;
            this.decision = null;
            this.note = '';
          }
          this.refreshCounts();
          this.lab
            .researchItem(it.plate)
            .then((fresh) =>
              this.apply(() => {
                if (!fresh) return;
                const merged = { ...updated, ...fresh, rank: fresh.rank ?? updated.rank };
                this.replaceItem(merged);
                if (this.selected && this.selected.plate === it.plate) this.selected = merged;
              })
            )
            .catch(() => {});
        })
      )
      .catch((e) => this.apply(() => (this.saveError = errText(e))))
      .finally(() => this.apply(() => (this.saving = false)));
  }

  private refreshCounts(): void {
    this.lab
      .research({ page: 1, page_size: 1 })
      .then((r) => this.apply(() => r && r.counts && this.setCounts(r.counts)))
      .catch(() => {});
  }

  private replaceItem(it: VlResearchItem): void {
    this.items = this.items.map((x) => (x.plate === it.plate ? it : x));
  }

  public openInScorecard(it: VlResearchItem): void {
    if (it.lot_key) this.openLot.emit(it.lot_key);
  }

  // =============================================================== formatting

  public findingOk(it: VlResearchItem | null): VlResearchFinding | null {
    const f = it && it.finding;
    return f && !f.error ? f : null;
  }

  public findingErr(it: VlResearchItem | null): string | null {
    const f = it && it.finding;
    return f && f.error ? f.error : null;
  }

  public statusLabel(it: VlResearchItem): string {
    switch (it.status) {
      case 'queued': return 'Queued';
      case 'owner_explained': return 'Your note – awaiting Claude';
      case 'research_requested': return 'Sent to AI research';
      case 'researched': return 'Researched – awaiting review';
      case 'error': return 'Error';
      case 'reviewed': return 'Reviewed: ' + this.decisionLabel(it.review ? it.review.decision : null);
      default: return it.status || '—';
    }
  }

  public statusClass(it: VlResearchItem): string {
    if (it.status === 'reviewed') return 'rs-rev-' + ((it.review && it.review.decision) || 'x');
    return 'rs-' + it.status;
  }

  public decisionLabel(d: VlResearchDecision | null | undefined): string {
    if (d === 'accepted') return 'accepted';
    if (d === 'rejected') return 'rejected';
    if (d === 'needs_more') return 'needs more';
    return '—';
  }

  public categoryLabel(c: string | null | undefined): string {
    if (!c) return '';
    const hit = RESEARCH_CATEGORIES.find((x) => x.id === c);
    return hit ? hit.label : c.replace(/_/g, ' ');
  }

  public engineLabel(it: VlResearchItem): string {
    if (it.range_method === 'cand') return 'Candidate';
    if (it.range_method === 'comp') return 'v1';
    return it.range_engine || '—';
  }

  /** Whole pounds → "£14,294". */
  public gbp(n: number | null | undefined): string {
    if (n === null || n === undefined || isNaN(n)) return '—';
    return '£' + Math.round(n).toLocaleString('en-GB');
  }

  public gbpShort(n: number | null | undefined): string {
    if (n === null || n === undefined || isNaN(n)) return '—';
    if (n >= 1e6) return '£' + trim1(n / 1e6) + 'm';
    if (n >= 1000) return '£' + trim1(n / 1000) + 'k';
    return '£' + Math.round(n);
  }

  public range(it: VlResearchItem, compact?: boolean): string {
    const c = compact === undefined ? this.isPhone : compact;
    const f = (n: number | null) => (c ? this.gbpShort(n) : this.gbp(n));
    if (it.low_gbp == null || it.high_gbp == null) return it.mid_gbp != null ? f(it.mid_gbp) : '—';
    return f(it.low_gbp) + '–' + f(it.high_gbp);
  }

  public ratio(r: number | null | undefined): string {
    if (r === null || r === undefined || isNaN(r)) return '—';
    return '×' + (r >= 10 ? Math.round(r * 10) / 10 : Math.round(r * 100) / 100);
  }

  public date(s: string | null | undefined): string {
    if (!s) return '';
    const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(s + 'T12:00:00') : new Date(s);
    return isNaN(d.getTime()) ? '' : d.getDate() + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear();
  }

  public dateTime(s: string | null | undefined): string {
    if (!s) return '';
    const d = new Date(s);
    if (isNaN(d.getTime())) return '';
    const p = (n: number) => (n < 10 ? '0' + n : '' + n);
    return this.date(s) + ', ' + p(d.getHours()) + ':' + p(d.getMinutes());
  }

  /** Only http(s) links are rendered as links. */
  public safeUrl(u: string | null | undefined): string | null {
    return u && /^https?:\/\//i.test(u) ? u : null;
  }

  public host(u: string): string {
    try {
      return new URL(u).hostname.replace(/^www\./, '');
    } catch {
      return u;
    }
  }

  public trackItem(_: number, it: VlResearchItem): string {
    return it.plate;
  }

  // ================================================================== helpers

  private apply(fn: () => any): void {
    this.zone.run(() => {
      fn();
      this.cdr.markForCheck();
    });
  }

  private scrollTo(ref: ElementRef<HTMLElement> | undefined): void {
    if (typeof window === 'undefined') return;
    setTimeout(() => {
      const el = ref && ref.nativeElement;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.top < 0 || r.top > window.innerHeight * 0.6) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }
}

function errText(e: any): string {
  return e instanceof VlError ? e.message : 'Valuation Lab data unavailable';
}

function normPlate(s: string): string {
  return (s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function trim1(n: number): string {
  return n >= 100 ? String(Math.round(n)) : String(Math.round(n * 10) / 10);
}
